"use client";

import { StarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { BANKS, type Bank } from "../banks";
import { CheckIcon } from "../CheckIcon";
import { OrderSummaryCard } from "../OrderSummaryCard";
import { SheenRing } from "../SheenRing";
import type { PackageSummary } from "../pricing";

const BENEFITS = [
  "100% Online – No branch visits required.",
  "Priority Onboarding – Exclusive fast-tracked review for Mutetaxes customers.",
  "Exclusive Rewards – Partner-only perks and welcome bonuses.",
];

// The export runs these two sentences together as
// "MSO Licensed PartnersRegulated by ..." — a missing separator, added here.
const MSO_NOTE = "MSO Licensed Partners. Regulated by HK Customs & Excise Department.";

function BankCard({
  bank,
  selected,
  onSelect,
}: {
  bank: Bank;
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  return (
    // The border is a gradient ring, not an outline: the label's own
    // background shows through the 1px (2px when selected) padding gap around
    // the opaque inner panel, and the spinning conic layer sits in that gap.
    <label
      className={cn(
        "group relative flex flex-1 cursor-pointer overflow-hidden rounded-[32px] bg-white/20 p-px transition-transform duration-300 motion-reduce:transition-none",
        // Lifts the chosen card slightly proud of the other. z-10 keeps its
        // edges above the sibling, which would otherwise paint over them since
        // both are positioned and the later one wins.
        selected && "z-10 scale-[1.03]",
      )}
    >
      <SheenRing selected={selected} />

      <input
        type="radio"
        name="banking-partner"
        value={bank.id}
        checked={selected}
        onChange={() => onSelect(bank.id)}
        className="peer sr-only"
      />

      <span className="relative flex w-full flex-col items-start gap-4 overflow-hidden rounded-[31px] bg-[#0F1319] px-2 py-5 peer-focus-visible:ring-2 peer-focus-visible:ring-inset peer-focus-visible:ring-white/60 sm:py-6">
        {/* Glow bleeding up from the lower edge of the clipped card */}
        <span
          aria-hidden
          className="pointer-events-none absolute -bottom-16 left-1/4 h-[125px] w-[189px] rounded-full bg-[#70A6FF] blur-[72px]"
        />
        {/* Its grain. A separate unblurred layer for the same reason as the
            image glow's, tinted to the glow's own blue and masked back to where
            the glow sits. Placed before the content so it stays behind the
            text, exactly like the glow it textures. */}
        <span
          aria-hidden
          className="bg-grain-glow grain-mask-bottom-glow pointer-events-none absolute inset-0 opacity-30"
        />

        {/* 350px per the design, matching the assets' own 349px width. Below
            that it goes fluid and keeps the artwork's 350:221 ratio rather than
            being cropped by overflow-hidden. The wrapper also clips the glow
            below to the image's rounded box. */}
        <span className="relative mx-auto block aspect-[350/221] w-full max-w-[350px] overflow-hidden rounded-2xl sm:aspect-auto sm:h-[221px] sm:w-[350px]">
          <img src={bank.image} alt="" className="h-full w-full object-cover" />

          {/* Airwallex only. Positioned as the design has it — a 189x125
              ellipse at left:-105, so it sits mostly outside the frame and only
              its blurred right edge washes over the image's left edge. */}
          {bank.imageGlow && (
            <>
              <span
                aria-hidden
                // A pale blue rather than the design's saturated #70A6FF: about
                // two thirds of the way from that toward white, so it reads as
                // a cool white glow with a hint of the brand blue in it.
                className="pointer-events-none absolute -left-[105px] top-[48px] h-[125px] w-[189px] rounded-full bg-[#CDE0FF]/70 blur-[72px]"
              />
              {/* Grain needs its own unblurred layer — inside the blurred glow
                  above it would just smear flat — so it spans the frame and is
                  masked back to the glow's footprint by grain-mask-glow.
                  Blended normally, not with `overlay`: overlay barely registers
                  against artwork this dark. */}
              <span
                aria-hidden
                className="bg-grain grain-mask-glow pointer-events-none absolute inset-0 opacity-30"
              />
            </>
          )}
        </span>

        <span className="relative flex w-full flex-col items-start gap-1 px-4">
          <span className="font-inter text-xl font-bold text-white sm:text-[22px]">{bank.name}</span>
          <span className="font-inter text-sm leading-[19.6px] text-[#9C9C9C]">{bank.bestFor}</span>
        </span>

        <span className="relative flex w-full flex-col items-start gap-1 px-4">
          <span className="font-inter text-sm leading-[19.6px] text-[#9C9C9C]">
            Mutetaxes Exclusive Perk
          </span>
          <span className="font-inter text-base font-bold text-white">{bank.perk}</span>
        </span>
      </span>
    </label>
  );
}

/**
 * Shared by both branches, whose banking screens are the same screen: same
 * partners, perks, benefits, MSO note, comparison link and "Why founders
 * choose …" panel. Only the heading and intro copy differ.
 */
export function BusinessBankingStep({
  bankId,
  onBankChange,
  summary,
  onSelectBank,
  onSkip,
  onCompareFeatures,
  heading = "Do you need business banking support?",
  intro = "Open accounts faster with trusted banking partners.",
}: {
  bankId: string | null;
  onBankChange: (id: string) => void;
  summary: PackageSummary;
  onSelectBank: () => void;
  onSkip: () => void;
  /** "Compare all features" has no destination in the design yet. */
  onCompareFeatures?: () => void;
  heading?: string;
  intro?: React.ReactNode;
}) {
  const selectedBank = BANKS.find((b) => b.id === bankId) ?? null;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (bankId) onSelectBank();
      }}
      className="mx-auto w-full max-w-[1250px] px-4 pb-16 pt-8 sm:px-2 sm:pb-24 sm:pt-12 lg:pt-[31px]"
    >
      <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
        {/* Left column — the design centres the non-stretching children */}
        <div className="flex min-w-0 flex-1 flex-col items-center gap-6 sm:gap-8">
          <div className="flex w-full flex-col items-start gap-3 sm:gap-4">
            <h1 className="font-suisse-intl-bold text-[26px] leading-tight text-white sm:text-4xl md:text-5xl lg:text-[50px] lg:leading-[71px]">
              {heading}
            </h1>
            <p className="font-suisse-intl-medium text-base leading-relaxed text-white sm:text-lg sm:leading-[28.8px]">
              {intro}
            </p>
          </div>

          {/* items-start, not items-center: these lines wrap on a phone, and
              centring parks the tick halfway down a two-line block. */}
          <ul className="flex w-full flex-col items-start gap-3 sm:gap-2">
            <li className="flex w-full items-start gap-[10px]">
              <CheckIcon className="relative mt-0.5 block h-6 w-6 shrink-0" />
              <span className="flex-1 font-inter text-sm font-bold leading-[19.6px] text-[#F3F4F6]">
                {BENEFITS[0]}
              </span>
            </li>
            {/* Unticked and mid-list, exactly as the export has it. */}
            <li className="w-full font-inter text-sm font-medium leading-[19.6px] text-[#F0F6FC]">
              We will refund the fees engaged within the first 30 days, should you be unhappy with
              our service.
            </li>
            {BENEFITS.slice(1).map((benefit) => (
              <li key={benefit} className="flex w-full items-start gap-[10px]">
                <CheckIcon className="relative mt-0.5 block h-6 w-6 shrink-0" />
                <span className="flex-1 font-inter text-sm font-bold leading-[19.6px] text-[#F3F4F6]">
                  {benefit}
                </span>
              </li>
            ))}
          </ul>

          <p className="w-full font-suisse-intl-medium text-sm italic leading-relaxed text-white sm:text-lg sm:leading-[28.8px] lg:w-auto lg:text-center">
            {MSO_NOTE}
          </p>

          {/* Stacked until lg: these cards carry a 350px-wide screenshot each,
              so two abreast below that squeezes the image and the perk copy. */}
          <fieldset className="flex w-full flex-col gap-5 lg:flex-row lg:items-stretch">
            <legend className="sr-only">Choose a banking partner</legend>
            {BANKS.map((bank) => (
              <BankCard
                key={bank.id}
                bank={bank}
                selected={bankId === bank.id}
                onSelect={onBankChange}
              />
            ))}
          </fieldset>

          <hr className="w-full border-t border-[#283143]" />

          <button
            type="button"
            onClick={onCompareFeatures}
            className="inline-flex items-center gap-2 font-inter text-base font-medium text-white underline transition-opacity hover:opacity-80"
          >
            <img src="/get-started/compare.svg" alt="" className="h-6 w-6 shrink-0" />
            Compare all features
          </button>

          {/* Shown only for a bank whose reasons are designed. */}
          {selectedBank && selectedBank.reasons.length > 0 && (
            <div className="flex w-full flex-col items-start gap-4 rounded-3xl bg-[#0E1116] p-5 outline outline-1 -outline-offset-1 outline-[#30363D] sm:p-6">
              <div className="flex w-full items-start gap-2 sm:items-center">
                <StarIcon
                  aria-hidden
                  className="mt-0.5 h-5 w-5 shrink-0 fill-[#FFC107] text-[#FFC107] sm:mt-0 sm:h-6 sm:w-6"
                />
                <h2 className="flex-1 font-inter text-lg font-bold leading-snug text-[#FBBF24] sm:text-2xl">
                  Why founders choose {selectedBank.name}
                </h2>
              </div>
              <ul className="flex w-full flex-col items-start gap-3 sm:gap-2">
                {selectedBank.reasons.map((reason) => (
                  <li key={reason} className="flex w-full items-start gap-[10px]">
                    <CheckIcon className="relative mt-0.5 block h-6 w-6 shrink-0" />
                    <span className="flex-1 font-inter text-sm font-bold leading-[19.6px] text-[#F3F4F6]">
                      {reason}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* col-reverse on phones so the primary action sits above the skip
              link despite coming second in the DOM. w-auto on the CTA from sm:
              its label is dynamic ("Select Airwallex"), so a fixed 188px would
              clip a longer partner name. */}
          <div className="flex w-full flex-col-reverse items-stretch gap-3 pt-4 sm:flex-row sm:items-center sm:justify-end sm:gap-4 sm:pt-6">
            <button
              type="button"
              onClick={onSkip}
              className="px-4 py-3 font-inter text-sm font-bold text-white transition-opacity hover:opacity-80"
            >
              Skip for now and set up later
            </button>
            <button
              type="submit"
              disabled={!selectedBank}
              className="flex h-14 w-full items-center justify-center rounded-full bg-white px-6 font-suisse-intl text-xl leading-5 text-[#2A4775] transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-50 sm:w-auto sm:min-w-[188px]"
            >
              {selectedBank ? `Select ${selectedBank.name}` : "Select a bank"}
            </button>
          </div>
        </div>

        {/* Right column — order summary. Banking carries no charge, so the
            total is unchanged by this step. */}
        <OrderSummaryCard summary={summary} className="lg:w-[402px] lg:shrink-0" />
      </div>
    </form>
  );
}
